import { useEffect } from 'react';
import { GetServerSideProps, GetServerSidePropsResult } from 'next';
import { DefaultLayout } from 'components/Layouts';
import { SearchPage, ViewType } from 'components/SearchPage/SearchPage';
import { searchPageApi } from 'api/searchPageApi';
import { sellerApi } from 'api/sellerApi';
import type { ISeller } from 'api/types/sellerApiTypes';
import type {
  ISearchPageFilter,
  ISearchPageAds,
  ISearchPageJatoAds,
  ISearchPageBreadcrumbs,
  IGetAdsRequestFilter,
  IGetAdsRequestRange,
  ISearchPagePaging,
  IDFP,
  IGTM,
  IGetSection,
} from 'api/types/searchPageApiTypes';
import { ResponseModelFilter, Notifications } from 'types';
import {
  ISearchPageSeoData,
  ISeoFaqSchema,
} from 'api/types/searchPageApiTypes';
import {
  getSectionParams,
  generateGetAdsRequestParams,
} from 'helpers/seo/searchParams';
import {
  createHiddenFilters,
  mapCountyList,
  mapGeoFilter,
  mapGeoFilterRequest,
  mapLocationDisplay,
  mapRangesRequest,
  mapSelectedCounyAndCoordinatesAndLocationDisplay,
} from 'components/SearchPage/mappers/filters';
import { getServerCookie } from 'utils';
import { cookies } from 'constants/cookies';
import { IQueryParams, ResponseMakeModelType } from 'types';
import { FeaturedDealer } from 'api/types/adviewApiTypes';
import { SearchFilterProvider } from 'components/SearchPage/context/SearchFilterContext';
import { logServerError } from 'helpers/logger';
import { PAGE } from 'helpers/pages';
import { SearchPageProvider } from 'components/SearchPage/context/SearchPageContext';
import { getVerticalFromBreadcrumbs } from 'helpers/verticals';
import { updateSiteFocusCookie } from 'helpers/updateSiteFocusCookie';
import { generateRecaptchaToken } from 'helpers/reCaptcha';
import StatusCode from 'status-code-enum';
import { LocationProvider } from 'features/location/Location.context';
import type { ListItem } from 'components/Toolkit/Inputs/CustomSelect';
import type { Coordinates } from 'features/location/Location.typed';

interface ISectionProps {
  faq: ISeoFaqSchema | null;
  ads: ISearchPageAds[];
  jatoAds: ISearchPageJatoAds[];
  spotlights: ISearchPageAds[];
  paging: ISearchPagePaging;
  filtersData: ISearchPageFilter[];
  filtersMakeModel: ResponseModelFilter;
  makeModelParams: ResponseMakeModelType;
  filtersQueryParams: IGetAdsRequestFilter[];
  rangeQueryParams: IGetAdsRequestRange[];
  section: string;
  sectionsData: IGetSection[];
  sort: string;
  keywordSearchTerm: string;
  breadcrumbs: ISearchPageBreadcrumbs[];
  notificationData?: Notifications;
  notFound: boolean;
  saved: boolean;
  seoData: ISearchPageSeoData;
  recommendations: ISearchPageAds[];
  userId?: string | string[];
  trader: ISeller | null;
  promoSlot?: FeaturedDealer | null;
  dfp: IDFP;
  gtm: IGTM;
  viewType: ViewType;
  sectionName: string;
  parentName: string | null;
  parentDisplayName: string | null;
  searchMatches: number;
  selectedCounty: ListItem | null;
  radius: string | null;
  locationDisplay: string;
  coordinates: Coordinates;
  countyTown: string | null;
  countyList: ListItem[];
  radiusList:
    | {
        label: string;
        value: string;
      }[];
}

interface ServerSideProps extends ISectionProps {
  domain?: string;
}

function Section(props: ServerSideProps) {
  const {
    domain = '',
    breadcrumbs,
    section,
    dfp,
    gtm,
    sectionsData,
    sort,
    ads,
    jatoAds,
    seoData,
    recommendations,
    spotlights,
    filtersData,
    filtersMakeModel,
    makeModelParams,
    filtersQueryParams,
    rangeQueryParams,
    keywordSearchTerm,
    paging,
    notificationData,
    notFound,
    userId,
    trader,
    promoSlot,
    saved,
    viewType,
    faq,
    sectionName,
    parentName,
    parentDisplayName,
    searchMatches,
    selectedCounty,
    radius,
    locationDisplay,
    coordinates,
    countyTown,
    countyList,
    radiusList,
  } = props;
  const vertical = getVerticalFromBreadcrumbs(breadcrumbs);
  updateSiteFocusCookie(vertical, domain);

  useEffect(() => {
    generateRecaptchaToken('search_page');
  }, []);

  return (
    <DefaultLayout
      domain={domain}
      notificationData={notificationData}
      vertical={vertical}
    >
      <SearchPageProvider
        sort={sort}
        section={section}
        keywordSearchTerm={keywordSearchTerm}
        paging={paging}
        viewType={viewType}
        saved={saved}
        userId={userId}
        baseUrl={domain}
        initialBreadcrumbs={breadcrumbs}
        initialRecommendations={recommendations}
      >
        <LocationProvider
          initialSelectedCounty={selectedCounty}
          initialRadius={radius}
          initialLocationDisplay={locationDisplay}
          initialCoordinates={coordinates}
          initialCountyTown={countyTown}
          countyList={countyList}
          radiusList={radiusList}
          page={PAGE.SEARCH}
        >
          <SearchFilterProvider
            filtersData={filtersData}
            filtersMakeModelData={filtersMakeModel}
            filtersQueryParams={filtersQueryParams}
            makeModelParams={makeModelParams}
            rangeQueryParams={rangeQueryParams}
          >
            <SearchPage
              initialValues={{
                sectionsData,
                filtersData,
                ads,
                jatoAds,
                spotlights: spotlights,
                seoData,
                dfp,
                gtm,
                sectionName,
                parentName,
                parentDisplayName,
                faq,
                searchMatches,
                recommendations,
              }}
              notFound={notFound}
              trader={trader}
              promoSlot={promoSlot}
            />
          </SearchFilterProvider>
        </LocationProvider>
      </SearchPageProvider>
    </DefaultLayout>
  );
}

const isEngine = (value: string) =>
  value.length === 3 && value.indexOf('.') == 1;
const mapEngine = (engineValue: string) =>
  isEngine(engineValue) ? `${engineValue.replace('.', '')}00` : engineValue;

/**
 * Temp mapper until we fully migrate search
 * @param queryParams
 */
export function mapLegacyQueryParams(queryParams: IQueryParams): IQueryParams {
  const newQueryParams = { ...queryParams };
  const keys = Object.keys(newQueryParams);

  for (const key of keys) {
    switch (key) {
      case 'engine_from': {
        const engineFrom = newQueryParams.engine_from as string;
        if (engineFrom) {
          newQueryParams.engine_from = mapEngine(engineFrom);
        }
        break;
      }
      case 'engine_to': {
        const engineTo = newQueryParams.engine_to as string;
        if (engineTo) {
          newQueryParams.engine_to = mapEngine(engineTo);
        }
        break;
      }
      case 'cash-offer': {
        newQueryParams.cashOffer = newQueryParams['cash-offer'];
        delete newQueryParams['cash-offer'];
        break;
      }
      case 'car-finance': {
        newQueryParams.carFinance = newQueryParams['car-finance'];

        if (newQueryParams.carFinance === 'yes') {
          newQueryParams.carFinance = 'true';
        }

        delete newQueryParams['car-finance'];
        break;
      }
      case 'source': {
        if (newQueryParams.source === 'trade') {
          newQueryParams.sellerType = 'pro';
        } else {
          newQueryParams.sellerType = newQueryParams.source;
        }
        delete newQueryParams.source;
        break;
      }
      case 'priceType': {
        if (newQueryParams.priceType === 'Pound') {
          if (newQueryParams.price_from) {
            newQueryParams.sterlingPrice_from = newQueryParams.price_from;
            delete newQueryParams.price_from;
          }
          if (newQueryParams.price_to) {
            newQueryParams.sterlingPrice_to = newQueryParams.price_to;
            delete newQueryParams.price_to;
          }
        }
        break;
      }
      case 'containsValidPrice': {
        if (newQueryParams.containsValidPrice !== 'true') {
          delete newQueryParams['containsValidPrice'];
        }

        break;
      }
    }
  }

  return newQueryParams;
}

export const getMakeModelFilters = async (
  section?: string,
  makeModelFilter?: ISearchPageFilter,
  makeModelParams?: ResponseMakeModelType,
  dealerId: string | null = null,
) => {
  let makeModelFiltersResponse: ResponseModelFilter = [];
  if (makeModelParams && makeModelFilter && section) {
    for (const makeModelParam of makeModelParams) {
      const selectedMakeFilter =
        (makeModelFilter.values &&
          makeModelFilter.values[0].all?.find(
            (valueItem) => valueItem.displayName === makeModelParam.make,
          )) ||
        [];
      if (selectedMakeFilter && !!makeModelParam.model) {
        const allModels = await searchPageApi.getModelByMake(
          section,
          makeModelParam.make,
          { accept: 'application/json' },
          dealerId,
        );
        allModels.data &&
          !makeModelFiltersResponse.find(
            (item) => item.makeName === makeModelParam.make,
          ) &&
          makeModelFiltersResponse.push({
            makeName: makeModelParam.make,
            ...allModels.data,
          });
      }
    }
  }
  return makeModelFiltersResponse;
};

export const getServerSideProps: GetServerSideProps<
  any,
  { section: string[] }
> = async (ctx): Promise<GetServerSidePropsResult<ServerSideProps>> => {
  // Temp map of query params to accommodate legacy URL
  const queryParams = mapLegacyQueryParams(ctx.query);
  const section = ctx.params?.section[0] || 'all';
  const keywordSearchTerms = queryParams.words
    ? Array.isArray(queryParams.words)
      ? queryParams.words[0]
      : queryParams.words
    : null;
  const notFound = typeof queryParams.notFound !== 'undefined';
  const saved = typeof queryParams.saved !== 'undefined';
  let trader: ISeller | null = null;
  const countyParam = queryParams.area
    ? Array.isArray(queryParams.area)
      ? queryParams.area
      : [queryParams.area]
    : null;
  const countyTownParam = queryParams.countyTown
    ? Array.isArray(queryParams.countyTown)
      ? queryParams.countyTown[0]
      : queryParams.countyTown
    : null;
  const radiusParam = queryParams.radius
    ? Array.isArray(queryParams.radius)
      ? queryParams.radius[0]
      : queryParams.radius
    : '0';

  // pull out user id
  const userId = queryParams.userId;

  try {
    const filters = await searchPageApi.getFilters(section);
    const filterData = [
      ...filters.data.showByDefault,
      ...createHiddenFilters(),
    ];

    const {
      isMakeModel,
      sectionQueryParams,
      makeModelParams,
      makeModelFilter,
      countyList,
      radiusList,
    } = getSectionParams(filterData, queryParams);

    if (userId) {
      const firstUser = Array.isArray(userId) ? userId[0] : userId;
      const { data } = await sellerApi.getAdvertiserProfile(firstUser);
      if (data.seller?.traderName) {
        trader = data.seller;
      }
    }

    const getAdsRequestParams = generateGetAdsRequestParams(
      section,
      {
        ...queryParams,
        ...sectionQueryParams,
      },
      filterData,
      userId,
    );

    const { selectedCounty, coordinates, locationDisplay } =
      await mapSelectedCounyAndCoordinatesAndLocationDisplay({
        sectionQueryParams,
        counties: countyList,
        countyParam,
        countyTownParam,
      });

    const locationData = mapGeoFilter({
      radius: radiusParam,
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      county: countyParam,
      countyTown: countyTownParam,
    });

    const geoFilter =
      locationData && countyTownParam ? locationData.geoFilter : undefined;

    // Only needed for initial API request.
    // DO NOT pass this to the client.
    const mappedGetAdsRequestParams = {
      geoFilter: mapGeoFilterRequest(geoFilter),
      ...getAdsRequestParams,
      ...(isMakeModel && {
        makeModelFilters: makeModelParams,
      }),
      ranges: getAdsRequestParams.ranges
        ? mapRangesRequest(getAdsRequestParams.ranges)
        : [],
    };

    const cookie = ctx.req.headers.cookie;

    const {
      data,
      headers: { unreadhistorychecks, unreadmessages, totalunread },
    } = await searchPageApi.getAds(mappedGetAdsRequestParams, {
      ...(cookie ? { cookie } : {}),
    });

    const sections = await searchPageApi.getSections(section, {
      ...mappedGetAdsRequestParams,
      terms: keywordSearchTerms,
    });

    const makeModelFiltersData = await getMakeModelFilters(
      section,
      makeModelFilter,
      makeModelParams,
    );

    const viewType = getServerCookie(cookies.VIEW_TYPE, ctx, false) || 'list';

    const legacyLocationDisplay = mapLocationDisplay(countyParam);

    const mappedCountyList: ListItem[] | null = countyList
      ? mapCountyList(countyParam, countyList)
      : null;

    return {
      props: {
        faq: data.seoFaqSchema?.faqSchema || null,
        paging: data.paging,
        keywordSearchTerm: getAdsRequestParams.terms || '',
        filtersData: filterData || [],
        filtersMakeModel: makeModelFiltersData || [],
        makeModelParams: makeModelParams || [],
        filtersQueryParams: getAdsRequestParams.filters || [],
        rangeQueryParams: getAdsRequestParams.ranges || [],
        ads: data.ads || [],
        jatoAds: data?.jatoAds || [],
        spotlights: data.spotlights || [],
        breadcrumbs: data.breadcrumbs || [],
        section,
        sectionsData: sections.data.subSections || [],
        seoData: data.seoData || { internalLinks: [] },
        recommendations: data.recommendations || [],
        sort: getAdsRequestParams.sort || '',
        saved: saved || false,
        userId: userId || '',
        trader,
        promoSlot: data.promoSlot ?? null,
        dfp: data.dataTargetingProps?.dfp || {},
        gtm: data.dataTargetingProps?.gtm || {},
        notificationData: {
          totalUnread: totalunread ?? null,
          unreadHistoryChecks: unreadhistorychecks ?? null,
          unreadMessages: unreadmessages ?? null,
        },
        notFound,
        viewType,
        sectionName: sections.data.displayName,
        parentName: sections.data.parentName || null,
        parentDisplayName: sections.data.parentDisplayName || null,
        searchMatches: sections.data.searchMatches || 0,
        selectedCounty: selectedCounty ?? null,
        radius: radiusParam,
        locationDisplay: legacyLocationDisplay || locationDisplay,
        coordinates,
        countyTown: countyTownParam,
        countyList: mappedCountyList || [],
        radiusList: radiusList || [],
      },
    };
  } catch (error) {
    ctx.req.log.error(logServerError(PAGE.SEARCH, error));

    if (error.code === 'ECONNABORTED') {
      // Timeout
      ctx.res.statusCode = StatusCode.ServerErrorGatewayTimeout;
    } else if (error?.response?.status === StatusCode.ClientErrorBadRequest) {
      // Bad request
      ctx.res.statusCode = StatusCode.ClientErrorBadRequest;
    } else {
      // Signal that there is an error
      ctx.res.statusCode = StatusCode.ServerErrorInternal;
    }

    return {
      props: {
        paging: {
          pageSize: 0,
          totalResults: 0,
          totalPages: 0,
          previousFrom: 0,
          nextFrom: 0,
          displayingTo: 0,
          displayingFrom: 0,
          currentPage: 0,
        },
        keywordSearchTerm: '',
        filtersData: [],
        filtersMakeModel: [],
        makeModelParams: [],
        filtersQueryParams: [],
        rangeQueryParams: [],
        ads: [],
        jatoAds: [],
        spotlights: [],
        breadcrumbs: [],
        section,
        promoSlot: null,
        sectionsData: [],
        seoData: {
          internalLinks: [],
        },
        recommendations: [],
        faq: null,
        sort: '',
        saved: false,
        userId: '',
        trader: null,
        dfp: {
          vertical: [],
          section: [],
          words: [],
          area: [],
          user_id: [],
        },
        gtm: {},
        notificationData: {
          totalUnread: 0,
          unreadHistoryChecks: 0,
          unreadMessages: 0,
        },
        notFound,
        viewType: 'list',
        sectionName: 'all',
        parentName: null,
        parentDisplayName: null,
        searchMatches: 0,
        selectedCounty: null,
        radius: null,
        locationDisplay: '',
        coordinates: {
          latitude: '',
          longitude: '',
        },
        countyTown: null,
        countyList: [],
        radiusList: [],
      },
    };
  }
};

export default Section;
